import { useFeatureFlags } from '@chronosphereio/core';
import { useMemo } from 'react';
import {
  generateTimeSeriesDataset,
  getSeriesIdGenerator,
  getTimeSeriesValues,
  isStackEnabled,
  isValidTruncationBandLabel,
  LOWER_BAND_TRUNCATION_SERIES_NAME,
  QueryMetadata,
  STACKED_BAND_TRUNCATION_SERIES_NAME,
  TimeScale,
  TimeSeries,
  TimeSeriesChartVisualOptions,
  TimeSeriesDataset,
  TimeSeriesTotals,
  TimeSeriesValueTuple,
  TruncationBandLabel,
  TruncationBandMaps,
  UPPER_BAND_TRUNCATION_SERIES_NAME,
} from '@/core';
import { useDataQueryResult } from '@/plugin-system';
import { getCommonTimeScaleForQueries } from '../utils/data-transform';
import { filterCommonLabels } from '../utils/filter-common-labels';

export interface UseProcessTimeSeriesDatasetProps {
  chartId: string;
  visual: TimeSeriesChartVisualOptions;
  enableStackSort?: boolean;
  /**
   * Required function for using the "group by" feature. It takes a single time series entry and should
   * return a string that associates it with a group (e.g. if grouping by a specific label, return the
   * value for that label). This will be used to generate groups and associate them with time series
   * data.
   *
   * This function can impact expensive memoized operations in the time series chart, so it is
   * recommended to be defined statically (i.e. outside the render cycle) or wrapped in `useCallback`
   * when possible to avoid performance issues.
   */
  getGroupKey?: (timeSeries: Omit<TimeSeriesDataset, 'groupKey'>) => string;
  /**
   * Whether to disable showing common labels separately.
   * See TimeSeriesChartPropsVizOnly for more details.
   */
  disableDeclutterCommonLabels?: boolean;
}

/**
 * Mapping of a group ids to arrays of matching time series ids for those groups
 */
export type TimeSeriesGroup = Record<string, Array<TimeSeriesDataset['id']>>;

export type TimeSeriesDatasetResponse = {
  /**
   * The dataset for the time series. This is used as an echarts dataset.
   * https://apache.github.io/echarts-handbook/en/concepts/dataset/
   */
  timeSeriesDataset: TimeSeriesDataset[];
  /**
   * Mapping of a group ids to arrays of matching time series ids for those groups
   *
   * This property is only set when the "group by" feature is configured.
   */
  timeSeriesGrouping?: TimeSeriesGroup;
  /**
   * Common time scale for all queries.
   */
  timeScale?: TimeScale;

  /**
   * When true, some of the series have formatted names.
   */
  hasFormattedNames: boolean;

  /**
   * When true, some of the series have icons.
   */
  hasSeriesIcons: boolean;

  /**
   * For stacked graphs, provide total value of each series per timestamp.
   * This can be used in places like the tooltip footer.
   */
  totalValuePerTimestamp?: TimeSeriesTotals;

  /**
   * Tooltip context menu data provided by the data query plugin as metadata.
   * Each Array index maps to a given query.
   */
  tooltipContextMenuData?: Array<QueryMetadata['tooltipContextMenu']>;

  /**
   * Array of renderSeriesIcon callbacks, each returning a custom legend icon for a series.
   */
  renderSeriesIconFns?: Array<QueryMetadata['renderSeriesIcon']>;

  /**
   * Common labels across all series.
   */
  commonLabels?: Record<string, string>;

  /**
   * Total number of series across all queries.
   */
  totalSeries: number;

  /**
   * Total number of datapoints across all series.
   */
  totalDatapoints: number;
};

const EMPTY_TIME_CHART_DATA_RESPONSE: TimeSeriesDatasetResponse = {
  timeSeriesDataset: [],
  hasFormattedNames: false,
  hasSeriesIcons: false,
  totalSeries: 0,
  totalDatapoints: 0,
};

const EMPTY_STATS = { totalSeries: 0, totalDatapoints: 0, isLoading: false };

/**
 * Processes query results and returns ECharts dataset, as well as information about the series data that would be expensive
 * to recalculate in the series processing (ex: nonconnectedDataPoints) that happens in TimeSeriesChartDatasetReady.
 * Dataset approach allows raw data to be handled separate from series option style properties.
 * See: https://apache.github.io/echarts-handbook/en/concepts/dataset/
 */
export function useProcessTimeSeriesDataset({
  chartId,
  visual,
  enableStackSort = true,
  getGroupKey,
  disableDeclutterCommonLabels = false,
}: UseProcessTimeSeriesDatasetProps): TimeSeriesDatasetResponse {
  const { enableTimeSeriesChartDeclutterCommonLabels } = useFeatureFlags();
  const { queryResults, stats, commonLabels } = useDataQueryResult({
    dataTypeFilter: 'TimeSeries',
    includeStats: true,
    includeCommonLabels: enableTimeSeriesChartDeclutterCommonLabels && !disableDeclutterCommonLabels,
  });
  const { totalSeries, totalDatapoints } = stats ?? EMPTY_STATS;
  const hasData = queryResults.some((result) => result.data && result.data.series?.length > 0);

  const timeScale = useMemo(() => getCommonTimeScaleForQueries(queryResults), [queryResults]);

  // Extract only the visual properties that affect dataset processing.
  // Ensures partial instead of full chart re-render when other visual options change.
  const visualStack = visual?.stack;
  const visualNullBehavior = visual?.null_behavior;

  // Process the time chart data, which is used as a dataset with echarts. Separating this from the
  // series processing enables us to only run this code when the data actually changes (instead of
  // also modifying it cases where the series changes like when we toggle items in the legend).
  const {
    timeSeriesDataset,
    timeSeriesGrouping,
    hasFormattedNames,
    hasSeriesIcons,
    totalValuePerTimestamp,
    tooltipContextMenuData,
    renderSeriesIconFns,
  } = useMemo(() => {
    if (!hasData || timeScale === undefined) {
      return EMPTY_TIME_CHART_DATA_RESPONSE;
    }

    const generateSeriesId = getSeriesIdGenerator();

    const timeSeriesDataset: TimeSeriesDataset[] = [];
    let sortedTimeSeriesDataset: TimeSeriesDataset[] | undefined;

    const shouldCalculateGrouping = !!getGroupKey;
    const timeSeriesGrouping: TimeSeriesGroup | undefined = shouldCalculateGrouping ? {} : undefined;

    let hasFormattedNames = false;
    let hasSeriesIcons = false;

    const tooltipContextMenuData: Array<QueryMetadata['tooltipContextMenu']> = new Array(queryResults.length);
    const renderSeriesIconFns: Array<QueryMetadata['renderSeriesIcon']> = new Array(queryResults.length);

    // Accumulate totals per timestamp for stacked graphs
    const isStacked = isStackEnabled(visualStack);
    let totalValuePerTimestamp: TimeSeriesTotals | undefined = undefined;

    // Truncation strategy adjustments to account for multiple queries
    let queriesTruncated = 0;
    for (const result of queryResults) {
      if (result.data?.metadata?.truncated) {
        queriesTruncated++;
      }
    }
    const multipleQueriesTruncated = queriesTruncated > 1;
    const truncatedSeriesTotalsMaps: TruncationBandMaps | undefined = multipleQueriesTruncated
      ? {
          stack: new Map(),
          lower: new Map(),
          upper: new Map(),
        }
      : undefined;
    let truncatedQueryIndex: number | undefined;

    if (isStacked && timeScale.stepMs !== undefined) {
      // Calculated totals to be used when calculating values in getTimeSeriesValues
      // Note: this can be removed when ECharts supports stackPercent option, see: https://github.com/apache/echarts/pull/20910/
      totalValuePerTimestamp = new Map();
      for (const result of queryResults) {
        if (result.isLoading || result.isFetching || result.data === undefined) continue;
        for (const timeSeries of result.data.series ?? []) {
          for (const [timestamp, value] of timeSeries.values) {
            if (value != null && value !== Infinity) {
              totalValuePerTimestamp.set(timestamp, (totalValuePerTimestamp.get(timestamp) ?? 0) + value);
            }
          }
        }
      }
    }

    for (let queryResultIndex = 0; queryResultIndex < queryResults.length; queryResultIndex++) {
      const result = queryResults[queryResultIndex];

      // Skip queries that are still loading or don't have data
      if (!result || result.isLoading || result.isFetching || result.data === undefined) continue;

      for (let i = 0; i < result.data.series.length; i++) {
        const timeSeries: TimeSeries | undefined = result.data.series[i];
        if (timeSeries === undefined) {
          return EMPTY_TIME_CHART_DATA_RESPONSE;
        }

        // Format is determined by series_name_format in query spec
        const formattedSeriesName = timeSeries.formattedName || timeSeries.name;

        // If the formatted name is not the name, series have formatted names. This helps us determine if it's worth
        // keeping the "Name" column when rendering labels-to-columns in the table legend.
        if (formattedSeriesName !== timeSeries.name) {
          hasFormattedNames = true;
        }

        if (result.data.metadata?.renderSeriesIcon) {
          hasSeriesIcons = true;
        }

        tooltipContextMenuData[queryResultIndex] = result.data.metadata?.tooltipContextMenu;
        renderSeriesIconFns[queryResultIndex] = result.data.metadata?.renderSeriesIcon;

        // We add a unique id for the chart to disambiguate items across charts when there are multiple on the page.
        // This id is also used by ExpandPanelProvider to select the correct legend item in expanded dialog view.
        // We need to normalize the series id to avoid bugs around special characters that may cause issues when
        // using it as a unique identifier.

        // Series id based on the chart and name
        const seriesId = generateSeriesId(chartId + formattedSeriesName);

        // Track the id of the series being compared to so we can apply styles separate from regular series.
        let compareToSeriesId: string | undefined;
        if (timeSeries.config?.compareTo !== undefined) {
          const compareToSeriesIndex = timeSeries.config.compareTo;
          if (compareToSeriesIndex !== null) {
            const compareToSeries = result.data.series[compareToSeriesIndex];
            if (compareToSeries) {
              compareToSeriesId = generateSeriesId(
                chartId + (compareToSeries.formattedName || compareToSeries.name),
                false // Do not deduplicate since we want the actual series id of without delimiter
              );
            }
          }
        }

        const truncationBandLabel = timeSeries.labels?.__truncation_band__;
        const isTruncated = multipleQueriesTruncated && isValidTruncationBandLabel(truncationBandLabel);

        const { values, nonconnectedDataPoints, calcs } = getTimeSeriesValues({
          series: timeSeries,
          includeCalcs: visualStack !== undefined || isTruncated,
          timeScale,
          visual: {
            stack: visualStack,
            null_behavior: visualNullBehavior,
          },
          totalValuePerTimestamp,
          truncatedSeriesTotals:
            isTruncated && truncatedSeriesTotalsMaps !== undefined
              ? { maps: truncatedSeriesTotalsMaps, band: truncationBandLabel }
              : undefined,
        });
        if (isTruncated && truncationBandLabel) {
          truncatedQueryIndex = queryResultIndex;
        } else {
          const dataset: TimeSeriesDataset = generateTimeSeriesDataset(values, {
            name: timeSeries.name,
            id: seriesId,
            formattedName: formattedSeriesName,
            queryResultIndex,
            config: timeSeries.config,
            labels: timeSeries.labels,
            compareToSeriesId,
            calcs,
            nonconnectedDataPoints,
          });

          // This MUST happen before the dataset gets pushed onto timeSeriesDataset because the
          // groupKey prop is set in here.
          if (shouldCalculateGrouping) {
            const groupKey = `grouped_${getGroupKey(dataset)}`;

            // Add the group key to the dataset for ease of association in legend code and other
            // locations that need to work with groups.
            dataset.groupKey = groupKey;

            if (timeSeriesGrouping) {
              if (!timeSeriesGrouping[groupKey]) {
                timeSeriesGrouping[groupKey] = [];
              }
              // Use of ! is safe because the line above validates that this key exists and is initialized.
              timeSeriesGrouping[groupKey]!.push(seriesId);
            }
          }

          timeSeriesDataset.push(dataset);
        }
      }
    }

    // Add condensedName property to datasets for tooltip display (legend keeps full name)
    if (commonLabels) {
      for (const dataset of timeSeriesDataset) {
        const displayName = dataset.formattedName ?? dataset.name;
        if (dataset.labels && typeof displayName === 'string') {
          const condensedName = filterCommonLabels({
            seriesName: displayName,
            labels: dataset.labels,
            commonLabels,
          });
          dataset.condensedName = condensedName;
        }
      }
    }

    // Consolidate all truncated series totals when multiple queries define truncation_strategy.
    if (truncatedSeriesTotalsMaps && truncatedQueryIndex !== undefined) {
      const pushTruncationBand = (band: TruncationBandLabel, name: string) => {
        const truncatedTotalsMap = truncatedSeriesTotalsMaps[band];
        if (!truncatedTotalsMap.size) return;

        const mergedValues: TimeSeriesValueTuple[] = [];
        let allTruncatedSeriesTotalValue = 0;
        for (const [timestamp, value] of truncatedTotalsMap) {
          allTruncatedSeriesTotalValue += value;
          mergedValues.push([timestamp, value]);
        }

        timeSeriesDataset.push(
          generateTimeSeriesDataset(mergedValues, {
            name,
            id: generateSeriesId(chartId + name, false),
            // Technically this synthetic series is the result of multiple queries.
            // Setting here since queryResultIndex is required.
            queryResultIndex: truncatedQueryIndex ?? 0,
            labels: { __truncated__: 'true' },
            config: { color: 'gray' },
            calcs: { totalValue: allTruncatedSeriesTotalValue },
          })
        );
      };

      // Stacked shows a single synthetic series with total of all truncated series at a given timestamp.
      // When not stacked and truncation limit is hit, we show consolidated lower and upper confidence bands.
      if (isStacked) {
        pushTruncationBand('stack', STACKED_BAND_TRUNCATION_SERIES_NAME);
      } else {
        pushTruncationBand('upper', UPPER_BAND_TRUNCATION_SERIES_NAME);
        pushTruncationBand('lower', LOWER_BAND_TRUNCATION_SERIES_NAME);
      }
    }

    if (isStacked && enableStackSort) {
      // Sort stack in descending order since ECharts renders the stack in reverse.
      // This means our largest series will be plotted first (at the bottom of the stack)
      // and smaller slices will show at the top.
      sortedTimeSeriesDataset = timeSeriesDataset.slice().sort((a, b) => {
        return (b.calcs?.totalValue ?? 0) - (a.calcs?.totalValue ?? 0);
      });
    }

    return {
      timeSeriesDataset: sortedTimeSeriesDataset ? sortedTimeSeriesDataset : timeSeriesDataset,
      timeSeriesGrouping,
      hasData,
      timeScale,
      hasFormattedNames,
      hasSeriesIcons,
      totalValuePerTimestamp,
      tooltipContextMenuData,
      renderSeriesIconFns,
    };
  }, [
    hasData,
    timeScale,
    getGroupKey,
    queryResults,
    visualStack,
    visualNullBehavior,
    enableStackSort,
    chartId,
    commonLabels,
  ]);

  // Filter out __name__ from common labels since represents the metric name
  const filteredCommonLabels = useMemo(() => {
    if (!commonLabels) return undefined;
    const { __name__, ...rest } = commonLabels;
    return Object.keys(rest).length > 0 ? rest : undefined;
  }, [commonLabels]);

  const processedDataset: TimeSeriesDatasetResponse = {
    timeSeriesDataset,
    timeSeriesGrouping,
    timeScale,
    hasFormattedNames,
    hasSeriesIcons,
    tooltipContextMenuData,
    renderSeriesIconFns,
    commonLabels: filteredCommonLabels,
    totalSeries,
    totalDatapoints,
  };

  // Totals are only included when graph is stacked.
  if (totalValuePerTimestamp !== undefined) {
    processedDataset.totalValuePerTimestamp = totalValuePerTimestamp;
  }

  return processedDataset;
}
